import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Trash2, ClipboardList, Paperclip, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const IMG = {
  extinguishers: "https://images.unsplash.com/photo-1625958936686-a9343dc35b5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  alarms: "https://images.unsplash.com/photo-1666734057863-0087ebfa44a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  suppression: "https://images.unsplash.com/photo-1772034736198-b194e4d72a1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  hydrants: "https://images.unsplash.com/photo-1650621031631-d4772e6a3182?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  safety: "https://images.unsplash.com/photo-1559656428-6f31733e0937?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
};

export default function Shop() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [note, setNote] = useState("");
  const [site, setSite] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [busy, setBusy] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  const add = (p) => setCart((c) => ({ ...c, [p.id]: { product: p, qty: (c[p.id]?.qty || 0) + 1 } }));
  const dec = (id) => setCart((c) => {
    const q = (c[id]?.qty || 0) - 1;
    if (q <= 0) { const n = { ...c }; delete n[id]; return n; }
    return { ...c, [id]: { ...c[id], qty: q } };
  });
  const remove = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const attachFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setAttachments((a) => [...a, data.path]);
      toast.success("Attachment added");
    } catch (err) { toast.error("Upload failed"); } finally { setUploadingFile(false); e.target.value = ""; }
  };
  const attSrc = (p) => fileUrl(p);

  const items = Object.values(cart);

  const submit = async () => {
    if (items.length === 0) return;
    setBusy(true);
    try {
      const payload = { items: items.map((i) => ({ product_id: i.product.id, name: i.product.name, qty: i.qty })), note, site_location: site, attachments };
      await api.post("/enquiries", payload);
      toast.success("Quotation request sent — our team will get back to you");
      setCart({}); setNote(""); setSite(""); setAttachments([]);
      navigate("/portal/orders");
    } catch (e) {
      toast.error("Could not send request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8">
      <PageHeader title="Request a Quotation" subtitle="Client portal" />
      <p className="text-sm text-muted-foreground -mt-4 mb-8 max-w-2xl">Add the products you need and send us a request. We'll reply with a quotation including product price, installation and transportation charges.</p>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="order-2 lg:order-1 lg:col-span-2 grid sm:grid-cols-2 gap-5">
          {products.map((p) => (
            <div key={p.id} className="bg-card border border-border rounded-sm overflow-hidden flex flex-col" data-testid={`shop-product-${p.id}`}>
              <div className="h-36 bg-muted"><img src={p.image_url || IMG[p.category]} alt={p.name} className="w-full h-full object-cover" /></div>
              <div className="p-4 flex flex-col flex-1">
                <Badge variant="outline" className="rounded-sm text-[10px] uppercase w-fit mb-2">{p.category}</Badge>
                <h3 className="font-semibold text-sm text-secondary leading-tight">{p.name}</h3>
                <div className="mt-auto pt-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Price on request</span>
                  <Button size="sm" onClick={() => add(p)} className="rounded-sm bg-secondary hover:bg-secondary/90 gap-1" data-testid={`add-to-cart-${p.id}`}><Plus size={14} /> Add</Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quote request panel */}
        <div className="order-1 lg:order-2 lg:sticky lg:top-8 h-fit bg-card border border-border rounded-sm p-6" data-testid="cart-panel">
          <button
            type="button"
            onClick={() => setPanelOpen((o) => !o)}
            className="w-full flex items-center justify-between gap-2 mb-0 lg:mb-5 lg:pointer-events-none"
            data-testid="cart-panel-toggle"
          >
            <div className="flex items-center gap-2"><ClipboardList size={18} className="text-primary" /><h3 className="font-heading font-bold text-lg text-secondary">Your Request ({items.length})</h3></div>
            <ChevronDown size={18} className={`text-muted-foreground shrink-0 transition-transform lg:hidden ${panelOpen ? "rotate-180" : ""}`} />
          </button>
          <div className={`${panelOpen ? "block mt-5 lg:mt-0" : "hidden"} lg:block`}>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No items yet. Add products to request a quotation.</p>
          ) : (
            <>
              <div className="space-y-3 mb-5 max-h-72 overflow-y-auto">
                {items.map((i) => (
                  <div key={i.product.id} className="flex items-center gap-2 text-sm" data-testid={`cart-item-${i.product.id}`}>
                    <div className="flex-1">
                      <p className="font-medium text-secondary leading-tight">{i.product.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{i.product.category}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => dec(i.product.id)} className="w-6 h-6 border border-border rounded-sm flex items-center justify-center hover:bg-muted"><Minus size={12} /></button>
                      <span className="w-6 text-center">{i.qty}</span>
                      <button onClick={() => add(i.product)} className="w-6 h-6 border border-border rounded-sm flex items-center justify-center hover:bg-muted"><Plus size={12} /></button>
                      <button onClick={() => remove(i.product.id)} className="ml-1 text-primary"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <Input value={site} onChange={(e) => setSite(e.target.value)} placeholder="Site location / address" className="rounded-sm text-sm mb-2" data-testid="enquiry-location" />
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add site details or special requirements (optional)…" className="rounded-sm text-sm" data-testid="enquiry-note" />
              <div className="mt-3">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-border text-sm cursor-pointer hover:bg-muted transition-colors" data-testid="attach-file-label">
                  <Paperclip size={14} /> {uploadingFile ? "Uploading…" : "Attach site drawings / photos"}
                  <input type="file" accept="image/*" className="hidden" onChange={attachFile} disabled={uploadingFile} data-testid="attach-file-input" />
                </label>
                {attachments.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {attachments.map((p, k) => (
                      <div key={k} className="relative">
                        <img src={attSrc(p)} alt="attachment" className="w-12 h-12 object-cover rounded-sm border border-border" />
                        <button onClick={() => setAttachments(attachments.filter((_, j) => j !== k))} className="absolute -top-1.5 -right-1.5 bg-primary text-white rounded-full w-4 h-4 flex items-center justify-center"><X size={10} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={submit} disabled={busy} className="w-full mt-4 rounded-sm bg-primary hover:bg-primary/90" data-testid="checkout-btn">
                {busy ? "Sending…" : "Send Quotation Request"}
              </Button>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
